import React, { useState } from 'react';

interface ApplicationApprovalModalProps {
    application: any;
    onClose: () => void;
    onSubmit: (applicationId: number, approved: boolean, notes?: string) => void;
}

const ApplicationApprovalModal: React.FC<ApplicationApprovalModalProps> = ({ application, onClose, onSubmit }) => {
    const [notes, setNotes] = useState('');
    const [sharingPreferences, setSharingPreferences] = useState({
        share_demographics: true,
        share_medical_history: true,
        share_medications: true,
        share_lab_results: true,
        share_imaging: true,
        share_notes: true
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setSharingPreferences(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleSubmit = (approved: boolean) => {
        onSubmit(application.id, approved, notes);
    };

    if (!application) return null;

    return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-user-check me-2 text-primary"></i>
                            Review Partner Application
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    
                    <div className="modal-body">
                        <div className="row g-3">
                            {/* Partner Information */}
                            <div className="col-12">
                                <h6 className="text-primary mb-3">Partner Information</h6>
                                <div className="card">
                                    <div className="card-body">
                                        <div className="row g-2">
                                            <div className="col-md-6">
                                                <small className="text-muted">Partner Name</small>
                                                <div className="fw-medium">{application.partner_name}</div>
                                            </div>
                                            <div className="col-md-6">
                                                <small className="text-muted">Specialty</small>
                                                <div className="fw-medium">{application.partner_specialty}</div>
                                            </div>
                                            <div className="col-md-6">
                                                <small className="text-muted">Experience</small>
                                                <div className="fw-medium">{application.partner_experience_years} years</div>
                                            </div>
                                            <div className="col-md-6">
                                                <small className="text-muted">Certifications</small>
                                                <div className="fw-medium">{application.partner_certifications}</div>
                                            </div>
                                            <div className="col-12">
                                                <small className="text-muted">Equipment & Facilities</small>
                                                <div className="fw-medium">{application.partner_equipment}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Proposed Terms */}
                            <div className="col-12">
                                <h6 className="text-primary mb-3">Proposed Terms</h6>
                                <div className="row g-2">
                                    <div className="col-md-6">
                                        <small className="text-muted">Rate Range</small>
                                        <div className="fw-medium">
                                            ${application.partner_minimum} - ${application.partner_maximum}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <small className="text-muted">Final Rate (if negotiated)</small>
                                        <div className="fw-medium">
                                            {application.final_rate ? `$${application.final_rate}` : 'Not set'}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <small className="text-muted">Patient Flow</small>
                                        <div className="fw-medium">
                                            {application.final_patient_flow || application.patient_flow} patients
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <small className="text-muted">Pay Mode</small>
                                        <div className="fw-medium text-capitalize">
                                            {application.final_pay_mode || application.pay_mode}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Information Sharing Preferences */}
                            <div className="col-12">
                                <h6 className="text-primary mb-3">Information Sharing Preferences</h6>
                                <div className="alert alert-info">
                                    <i className="fas fa-info-circle me-2"></i>
                                    <strong>Important:</strong> You control what information is shared with this partner.
                                </div>
                                <div className="row g-2">
                                    <div className="col-md-6">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="share_demographics"
                                                checked={sharingPreferences.share_demographics}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label">
                                                <strong>Demographics</strong>
                                                <br />
                                                <small className="text-muted">Age, gender, contact information</small>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="share_medical_history"
                                                checked={sharingPreferences.share_medical_history}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label">
                                                <strong>Medical History</strong>
                                                <br />
                                                <small className="text-muted">Past conditions, surgeries, allergies</small>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="share_medications"
                                                checked={sharingPreferences.share_medications}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label">
                                                <strong>Current Medications</strong>
                                                <br />
                                                <small className="text-muted">Prescriptions, dosages, frequency</small>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="share_lab_results"
                                                checked={sharingPreferences.share_lab_results}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label">
                                                <strong>Lab Results</strong>
                                                <br />
                                                <small className="text-muted">Blood tests, urine tests, etc.</small>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="share_imaging"
                                                checked={sharingPreferences.share_imaging}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label">
                                                <strong>Imaging</strong>
                                                <br />
                                                <small className="text-muted">X-rays, MRIs, CT scans</small>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="share_notes"
                                                checked={sharingPreferences.share_notes}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label">
                                                <strong>Clinical Notes</strong>
                                                <br />
                                                <small className="text-muted">Doctor's notes, observations</small>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="col-12">
                                <label className="form-label">
                                    <i className="fas fa-comment me-2"></i>
                                    Additional Notes (Optional)
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Any additional comments about this partner or your decision..."
                                />
                            </div>
                        </div>
                    </div>
                    
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-danger me-2" 
                            onClick={() => handleSubmit(false)}
                        >
                            <i className="fas fa-times me-2"></i>
                            Reject Partner
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-success" 
                            onClick={() => handleSubmit(true)}
                        >
                            <i className="fas fa-check me-2"></i>
                            Approve Partner
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicationApprovalModal; 